<template>
  <div class="dialog-details">
    <v-card>
      <v-card-title class="pb-2">
        <div style="width: 100%">
          <v-row dense no-gutters align="start">
            <v-col cols="12" md="3" class="pr-md-4 mb-3 mb-md-0">
              <div v-if="imagePreviewUrl" class="header-image">
                <v-img
                  :src="imagePreviewUrl"
                  alt="aperçu"
                  contain
                  height="140"
                ></v-img>
              </div>
              <div
                v-else
                class="header-image placeholder d-flex align-center justify-center text-caption"
              >
                Aucune image
              </div>
            </v-col>
            <v-col cols="12" md="9">
              <div class="text-overline primary--text mb-n1">
                {{ item.site_name }}
                <span class="grey--text text--darken-1"
                  >(ID: {{ item.site_id }})</span
                >
              </div>

              <!-- Case 3: Title is generated -->
              <div v-if="generatedTitle" class="mb-1 header-text-wrap">
                <div class="text-h6 font-weight-bold">
                  {{ generatedTitle }}
                </div>
                <div class="text-body-2 grey--text text--darken-1">
                  Angle : {{ item.topic }}
                </div>
              </div>

              <!-- Case 1 & 2: No title yet -->
              <div
                v-else
                class="text-h6 font-weight-bold mb-1 header-text-wrap"
              >
                {{ item.topic }}
              </div>

              <div class="d-flex flex-wrap align-center chips-row">
                <v-chip
                  small
                  :color="statusColor(item.status)"
                  dark
                  class="mr-2 mb-2"
                >
                  {{ item.status }}
                </v-chip>
                <v-chip
                  v-if="item.retry_count > 0"
                  small
                  class="mr-2 mb-2"
                  outlined
                  color="warning"
                >
                  <v-icon x-small left>{{ icons.mdiRestart }}</v-icon>
                  Relances auto : {{ item.retry_count }}
                </v-chip>
                <v-chip
                  v-if="item.post_status"
                  small
                  class="mr-2 mb-2"
                  outlined
                  color="primary"
                >
                  Publish status : {{ item.post_status }}
                </v-chip>
                <v-chip
                  v-if="item.image_handler"
                  small
                  class="mr-2 mb-2"
                  outlined
                  :color="imageMode.color"
                  :title="item.image_handler"
                >
                  <v-icon x-small left>{{ imageMode.icon }}</v-icon>
                  Image : {{ imageMode.label }}
                </v-chip>
                <v-chip
                  v-if="templateId"
                  small
                  outlined
                  color="primary"
                  class="mr-2 mb-2"
                  :to="canOpenTemplateConfig ? templateRoute : null"
                  :target="canOpenTemplateConfig ? '_blank' : null"
                  :rel="canOpenTemplateConfig ? 'noopener noreferrer' : null"
                >
                  Template : {{ templateName }}
                  <v-icon v-if="canOpenTemplateConfig" x-small right>
                    {{ icons.mdiOpenInNew }}
                  </v-icon>
                </v-chip>
                <v-chip
                  v-if="item.topic_is_title"
                  small
                  class="mr-2 mb-2"
                  outlined
                  color="info"
                >
                  L'angle est le titre
                </v-chip>
                <v-chip
                  v-if="item.current_step"
                  small
                  outlined
                  color="primary"
                  class="mr-2 mb-2"
                >
                  Dernière étape effectuée :
                  {{ formatStepName(item.current_step) }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-alert
          v-if="item.status === 'error' && payload && payload.error"
          type="error"
          prominent
          class="mb-4"
        >
          <div class="text-subtitle-1 font-weight-bold mb-1">Erreur de génération :</div>
          {{ payload.error }}
        </v-alert>

        <v-alert
          v-if="item.warning"
          type="warning"
          dense
          outlined
          class="mb-4"
        >
          {{ item.warning }}
        </v-alert>
        <v-row dense class="mb-4">
          <v-col cols="12" md="4">
            <div class="text-caption text--secondary mb-1">Utilisateur</div>
            <div class="text-body-2">
              {{ item.user_name }}
              <span class="grey--text text--darken-1"
                >({{ item.user_email }})</span
              >
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption text--secondary mb-1">Créé</div>
            <div class="text-body-2">
              {{ formatDateHumanReadable(item.created) }}
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption text--secondary mb-1">Modifié</div>
            <div class="text-body-2">
              {{ formatDateHumanReadable(item.updated) }}
            </div>
          </v-col>
        </v-row>

        <div class="mb-6">
          <!-- <div class="d-flex align-center mb-2">
            <div class="text-subtitle-1 font-weight-medium">
              Étapes
              <v-chip v-if="devMode" x-small color="error" class="ml-2"
                >DEV MODE</v-chip
              >
            </div>
            <v-spacer />
            <v-chip small outlined class="mr-2">
              {{ visibleSteps.length }} étape{{
                visibleSteps.length > 1 ? "s" : ""
              }}
            </v-chip>
          </div> -->

          <v-alert v-if="!visibleSteps.length" type="info" dense outlined>
            Aucune chronologie disponible pour cet item.
          </v-alert>

          <div v-else class="step-wrapper pa-3">
            <div class="d-flex align-center mb-1">
              <v-btn icon small @click="prevStep" :disabled="activeStep === 0">
                <v-icon>{{ icons.mdiChevronLeft }}</v-icon>
              </v-btn>

              <v-tabs
                v-model="activeStep"
                background-color="transparent"
                grow
                show-arrows
                class="flex-grow-1 step-tabs custom-tabs"
                hide-slider
              >
                <v-tab
                  v-for="(step, index) in visibleSteps"
                  :key="step.name"
                  class="text-capitalize"
                  :class="{ 'disabled-step': isStepDisabled(step) }"
                  :style="{
                    'border-top': getStepTopBorderColor(step)
                      ? `4px solid ${getStepTopBorderColor(step)}`
                      : 'none',
                  }"
                >
                  <div class="d-flex align-center tab-content">
                    <v-icon
                      v-if="
                        item.status === 'error' &&
                        item.current_step === step.name &&
                        step.status === 'error'
                      "
                      small
                      class="mr-1 tab-icon"
                      color="error"
                    >
                      {{ icons.mdiAlertCircleOutline }}
                    </v-icon>
                    <span class="tab-label">{{ step.label }}</span>
                    <v-tooltip v-if="getStepStage(step.name)" top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          x-small
                          class="ml-1 grey--text text--lighten-1"
                        >
                          {{ icons.mdiCog }}
                        </v-icon>
                      </template>
                      <span>Stage {{ getStepStage(step.name) }}</span>
                    </v-tooltip>
                  </div>
                </v-tab>
              </v-tabs>

              <v-btn
                icon
                small
                @click="nextStep"
                :disabled="activeStep >= visibleSteps.length - 1"
              >
                <v-icon>{{ icons.mdiChevronRight }}</v-icon>
              </v-btn>
            </div>

            <v-tabs-items v-model="activeStep" class="mt-2">
              <v-tab-item
                v-for="(step, index) in visibleSteps"
                :key="`${step.name}-${index}`"
              >
                <v-card flat outlined class="pa-4">
                  <v-alert
                    v-if="
                      item.status === 'error' &&
                      item.current_step === step.name &&
                      payload.error
                    "
                    type="error"
                    prominent
                    class="mb-4"
                  >
                    {{ payload.error }}
                  </v-alert>

                  <div class="d-flex flex-wrap align-center mb-3">
                    <v-chip small class="mr-2 mb-2" outlined>
                      Début :
                      <strong class="ml-1">{{
                        formatDateHumanReadable(step.start)
                      }}</strong>
                    </v-chip>
                    <v-chip
                      small
                      class="mr-2 mb-2"
                      outlined
                      :color="step.end ? '' : 'grey lighten-3'"
                    >
                      Fin :
                      <strong class="ml-1">{{
                        step.end ? formatDateHumanReadable(step.end) : "—"
                      }}</strong>
                    </v-chip>
                    <v-chip
                      v-if="step.duration"
                      small
                      color="success"
                      outlined
                      class="mr-2 mb-2"
                    >
                      Durée : {{ step.duration }}
                    </v-chip>
                  </div>

                  <!-- SPECIAL DISPLAY FOR INIT STEP -->
                  <div v-if="step.name === 'init'">
                    <v-alert
                      v-if="!step.details.length"
                      dense
                      outlined
                      type="info"
                    >
                      Pas de métadonnées pour cette étape.
                    </v-alert>
                    <div v-else>
                      <div
                        v-for="(detail, dIdx) in step.details"
                        :key="`init-det-${dIdx}`"
                        class="mb-3"
                      >
                        <!-- Summary View -->
                        <div v-if="detail.site_connector" class="mb-4">
                          <div class="text-subtitle-2 mb-2">Configuration</div>
                          <v-simple-table dense>
                            <tbody>
                              <tr>
                                <td class="font-weight-medium">Site ID</td>
                                <td>{{ detail.site_connector.site_id }}</td>
                              </tr>
                              <tr>
                                <td class="font-weight-medium">Site URL</td>
                                <td>
                                  <a
                                    :href="detail.site_connector.site_url"
                                    target="_blank"
                                    >{{ detail.site_connector.site_url }}</a
                                  >
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </div>

                        <div v-if="detail.site_llm_configs" class="mb-4">
                          <div class="text-subtitle-2 mb-2">
                            Modèles LLM par stage
                          </div>
                          <v-simple-table dense>
                            <tbody>
                              <tr
                                v-for="stage in [
                                  'stage1',
                                  'stage2',
                                  'stage3',
                                  'stage4',
                                ]"
                                :key="stage"
                              >
                                <td class="font-weight-medium">
                                  {{ stage.replace("stage", "Stage ") }}
                                </td>
                                <td>
                                  {{
                                    detail.site_llm_configs[`${stage}_model`] ||
                                    "—"
                                  }}
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </div>

                        <!-- Full JSON Details (Only in DEV MODE) -->
                        <div v-if="devMode">
                          <div class="text-caption grey--text mb-1">
                            Raw Details (Dev Mode)
                          </div>
                          <pre class="code-block">{{ prettyJson(detail) }}</pre>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- SPECIAL DISPLAY FOR SOURCES STEP -->
                  <div v-else-if="step.name === 'get_sources_text'">
                    <div class="mb-4">
                      <div class="text-subtitle-2 mb-2 d-flex align-center">
                        Sources collectées
                        <v-chip small class="ml-2" outlined>
                          {{ sourcesCount }} source{{
                            sourcesCount > 1 ? "s" : ""
                          }}
                        </v-chip>
                      </div>
                      <v-alert
                        v-if="!payload.sources || !payload.sources.length"
                        dense
                        outlined
                        type="info"
                        class="mb-0"
                      >
                        Aucune source disponible pour cet item.
                      </v-alert>
                      <v-expansion-panels
                        v-else
                        multiple
                        accordion
                        class="source-panels"
                      >
                        <v-expansion-panel
                          v-for="(source, index) in payload.sources"
                          :key="`${source.url || 'source'}-${index}`"
                        >
                          <v-expansion-panel-header>
                            Source n°{{ index + 1 }} -&nbsp;
                            <!--
                              Le time code dit sur QUEL passage de la video
                              l'article a ete ecrit. Sans lui, deux items issus
                              de deux angles d'une meme video sont
                              indiscernables dans cet ecran.
                            -->
                            <v-chip
                              v-if="sourceTimecode(source)"
                              x-small
                              outlined
                              class="ml-2 flex-grow-0 flex-shrink-0"
                            >
                              {{ sourceTimecode(source) }}
                            </v-chip>
                            <span
                              v-if="source.url"
                              class="ml-2 grey--text text-truncate"
                              style="max-width: 70%"
                            >
                              {{ source.url }}
                            </span>
                            <span
                              v-else
                              class="ml-2 grey--text text-truncate"
                              style="max-width: 70%"
                            >
                              Texte: {{ truncateText(source.content) }}
                            </span>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <div v-if="source.url" class="mb-2">
                              <a
                                :href="source.url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="text-decoration-none"
                              >
                                Ouvrir la source
                              </a>
                            </div>
                            <!--
                              `content` est vide par construction sur les
                              sources scrapees (video comprise) : le texte est
                              recupere a la generation, pas a la mise en file.
                              On affiche donc le passage REELLEMENT utilise,
                              extrait du texte consolide — sinon l'ecran disait
                              « Texte non fourni » sur des items parfaitement
                              corrects.
                            -->
                            <div
                              v-if="source.content || sourceUsedText(index)"
                              class="scroll-block"
                            >
                              <pre class="code-block">{{
                                source.content || sourceUsedText(index)
                              }}</pre>
                            </div>
                            <div v-else class="grey--text text--darken-1">
                              Texte non fourni
                            </div>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>

                    <!-- Consolidated Text & Details -->
                    <div v-if="payload.sources_text || step.details.length">
                      <v-divider class="my-4" />
                      <div class="text-caption grey--text mb-2">
                        Métadonnées techniques
                      </div>
                      <v-expansion-panels multiple focusable>
                        <!-- Consolidated Text Panel -->
                        <v-expansion-panel v-if="payload.sources_text">
                          <v-expansion-panel-header>
                            Texte consolidé
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <div class="scroll-block code-block mt-2">
                              {{ payload.sources_text }}
                            </div>
                          </v-expansion-panel-content>
                        </v-expansion-panel>

                        <!-- Standard Details Loop -->
                        <v-expansion-panel
                          v-for="(detail, detailIndex) in step.details"
                          :key="detailIndex"
                        >
                          <v-expansion-panel-header>
                            Détails techniques
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre class="code-block">{{
                              prettyJson(detail)
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>

                  <!-- SPECIAL DISPLAY FOR ANALYSE STEP -->
                  <div v-else-if="step.name === 'analyse_sources'">
                    <div class="text-subtitle-2 mb-2">Analyse SEO / SERP</div>
                    <div class="scroll-block code-block mb-4">
                      {{ payload.sources_analysed_text || "—" }}
                    </div>

                    <div v-if="llmConfigs.stage1_model" class="mt-4 mb-4">
                      <div class="text-caption grey--text mb-2">
                        Configuration LLM (Stage 1)
                      </div>
                      <div class="text-body-2 mb-2">
                        Modèle : <strong>{{ llmConfigs.stage1_model }}</strong>
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-if="llmConfigs.stage1_system_prompt"
                        >
                          <v-expansion-panel-header class="text-caption"
                            >System Prompt</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <pre class="code-block mt-2">{{
                              llmConfigs.stage1_system_prompt
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel v-if="llmConfigs.stage1_user_prompt">
                          <v-expansion-panel-header class="text-caption"
                            >User Prompt Template</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <pre class="code-block mt-2">{{
                              llmConfigs.stage1_user_prompt
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>

                    <!-- Default raw details if any -->
                    <div v-if="step.details.length">
                      <v-divider class="my-4" />
                      <div class="text-caption grey--text mb-2">
                        Métadonnées techniques
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-for="(detail, detailIndex) in step.details"
                          :key="detailIndex"
                        >
                          <v-expansion-panel-header>
                            Détails techniques
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre class="code-block">{{
                              prettyJson(detail)
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>

                  <!-- SPECIAL DISPLAY FOR CONTENU STEP -->

                  <div v-else-if="step.name === 'generate_post_content'">
                    <div class="text-subtitle-2 mb-2">
                      Contenu généré (HTML)
                    </div>

                    <div
                      v-if="payload.post_content"
                      class="html-preview scroll-block mb-4"
                      v-html="payload.post_content"
                    ></div>

                    <v-alert v-else dense outlined type="info" class="mb-4">
                      Aucun contenu généré n'est disponible.
                    </v-alert>

                    <div v-if="llmConfigs.stage2_model" class="mt-4 mb-4">
                      <div class="text-caption grey--text mb-2">
                        Configuration LLM (Stage 2)
                      </div>

                      <div class="text-body-2 mb-2">
                        Modèle : <strong>{{ llmConfigs.stage2_model }}</strong>
                      </div>

                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-if="llmConfigs.stage2_system_prompt"
                        >
                          <v-expansion-panel-header class="text-caption"
                            >System Prompt</v-expansion-panel-header
                          >

                          <v-expansion-panel-content>
                            <pre class="code-block mt-2">{{
                              llmConfigs.stage2_system_prompt
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>

                        <v-expansion-panel v-if="llmConfigs.stage2_user_prompt">
                          <v-expansion-panel-header class="text-caption"
                            >User Prompt Template</v-expansion-panel-header
                          >

                          <v-expansion-panel-content>
                            <pre class="code-block mt-2">{{
                              llmConfigs.stage2_user_prompt
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>

                    <!-- Default raw details if any -->
                    <div v-if="step.details.length">
                      <v-divider class="my-4" />
                      <div class="text-caption grey--text mb-2">
                        Métadonnées techniques
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-for="(detail, detailIndex) in step.details"
                          :key="detailIndex"
                        >
                          <v-expansion-panel-header>
                            Détails techniques
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre class="code-block">{{
                              prettyJson(detail)
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>

                  <!-- SPECIAL DISPLAY FOR EXTRAS STEP -->
                  <div v-else-if="step.name === 'generate_post_extra'">
                    <div class="text-subtitle-2 mb-2">SEO / Extras générés</div>
                    <div class="mb-3">
                      <div class="text-caption text--secondary">Titre</div>
                      <div class="text-body-2 font-weight-medium">
                        {{ generatedTitle || "—" }}
                      </div>
                    </div>
                    <div class="mb-3">
                      <div class="text-caption text--secondary">Chapô</div>
                      <div class="text-body-2">
                        {{ postExtras ? postExtras.excerpt : "—" }}
                      </div>
                    </div>
                    <div class="mb-3">
                      <div class="text-caption text--secondary">
                        Meta description
                      </div>
                      <div class="text-body-2">
                        {{ postExtras ? postExtras.meta_description : "—" }}
                      </div>
                    </div>
                    <div
                      v-if="
                        postExtras && postExtras.summary_bloc_bullet_point_1
                      "
                      class="mt-2"
                    >
                      <div class="text-caption text--secondary mb-1">
                        Bloc résumé
                      </div>
                      <ul class="pl-4 mb-0">
                        <li v-if="postExtras.summary_bloc_bullet_point_1">
                          {{ postExtras.summary_bloc_bullet_point_1 }}
                        </li>
                        <li v-if="postExtras.summary_bloc_bullet_point_2">
                          {{ postExtras.summary_bloc_bullet_point_2 }}
                        </li>
                        <li v-if="postExtras.summary_bloc_bullet_point_3">
                          {{ postExtras.summary_bloc_bullet_point_3 }}
                        </li>
                      </ul>
                    </div>

                    <div v-if="llmConfigs.stage3_model" class="mt-4 mb-4">
                      <div class="text-caption grey--text mb-2">
                        Configuration LLM (Stage 3)
                      </div>
                      <div class="text-body-2 mb-2">
                        Modèle : <strong>{{ llmConfigs.stage3_model }}</strong>
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-if="llmConfigs.stage3_system_prompt"
                        >
                          <v-expansion-panel-header class="text-caption"
                            >System Prompt</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <pre class="code-block mt-2">{{
                              llmConfigs.stage3_system_prompt
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel v-if="llmConfigs.stage3_user_prompt">
                          <v-expansion-panel-header class="text-caption"
                            >User Prompt Template</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <pre class="code-block mt-2">{{
                              llmConfigs.stage3_user_prompt
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>

                    <!-- Default raw details if any -->
                    <div v-if="step.details.length">
                      <v-divider class="my-4" />
                      <div class="text-caption grey--text mb-2">
                        Métadonnées techniques
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-for="(detail, detailIndex) in step.details"
                          :key="detailIndex"
                        >
                          <v-expansion-panel-header>
                            Détails techniques
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre class="code-block">{{
                              prettyJson(detail)
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>

                  <!-- SPECIAL DISPLAY FOR INTERNAL LINKS STEP -->
                  <div v-else-if="step.name === 'generate_post_internal_links'">
                    <v-row dense class="mb-4">
                      <v-col cols="12" md="6">
                        <div class="text-caption text--secondary">
                          Liens appliqués
                        </div>
                        <div
                          class="text-body-1 font-weight-bold mb-0 primary--text"
                        >
                          {{ internalLinksInfo.applied }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="6">
                        <div class="text-caption text--secondary">
                          Candidats identifiés
                        </div>
                        <div class="text-body-1 font-weight-bold mb-0">
                          {{ internalLinksInfo.candidates_count }}
                        </div>
                      </v-col>
                    </v-row>

                    <!-- Search Topics -->
                    <div
                      v-if="
                        internalLinksInfo.topics &&
                        internalLinksInfo.topics.length
                      "
                      class="mb-6"
                    >
                      <div class="text-subtitle-2 mb-2">
                        Sujets de recherche
                      </div>
                      <div class="d-flex flex-wrap">
                        <v-chip
                          v-for="(topic, tIdx) in internalLinksInfo.topics"
                          :key="`topic-${tIdx}`"
                          small
                          outlined
                          class="mr-2 mb-2"
                        >
                          {{ topic }}
                        </v-chip>
                      </div>
                    </div>

                    <!-- Integrated Articles List -->
                    <div
                      v-if="
                        internalLinksInfo.integratedArticles &&
                        internalLinksInfo.integratedArticles.length
                      "
                      class="mb-4"
                    >
                      <div class="text-subtitle-2 mb-2">Articles intégrés</div>
                      <v-card outlined>
                        <v-list dense>
                          <template
                            v-for="(
                              article, articleIndex
                            ) in internalLinksInfo.integratedArticles"
                          >
                            <v-list-item :key="`integrated-${articleIndex}`">
                              <v-list-item-content>
                                <v-list-item-title
                                  class="text-body-2 font-weight-medium mb-1"
                                >
                                  <a
                                    :href="article.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="text-decoration-none"
                                  >
                                    {{ article.title || article.url }}
                                  </a>
                                </v-list-item-title>

                                <div class="d-flex flex-wrap align-center mb-1">
                                  <v-chip
                                    x-small
                                    label
                                    outlined
                                    color="grey darken-2"
                                    class="mr-2 mb-1"
                                  >
                                    <v-icon left x-small>{{
                                      icons.mdiFolderOutline
                                    }}</v-icon>
                                    {{ article.category || "Sans catégorie" }}
                                  </v-chip>

                                  <v-chip
                                    v-for="(
                                      articleTopic, topicIndex
                                    ) in article.topics || []"
                                    :key="`integrated-topic-${articleIndex}-${topicIndex}`"
                                    x-small
                                    color="primary"
                                    outlined
                                    class="mr-1 mb-1"
                                  >
                                    {{ articleTopic }}
                                  </v-chip>
                                </div>

                                <div
                                  v-if="article.keywords"
                                  class="text-caption grey--text text--darken-1 mb-1"
                                >
                                  <span class="font-italic">Mots-clés : </span>
                                  {{ article.keywords }}
                                </div>

                                <div
                                  class="text-caption grey--text text--lighten-1 text-truncate"
                                >
                                  {{ article.url }}
                                </div>
                              </v-list-item-content>
                            </v-list-item>
                            <v-divider
                              v-if="
                                articleIndex <
                                internalLinksInfo.integratedArticles.length - 1
                              "
                              :key="`integrated-divider-${articleIndex}`"
                            ></v-divider>
                          </template>
                        </v-list>
                      </v-card>
                    </div>

                    <v-alert
                      v-else-if="internalLinksInfo.applied > 0"
                      type="info"
                      dense
                      outlined
                      class="mb-4"
                    >
                      Le détail des articles intégrés n’est pas disponible pour
                      cette ancienne génération.
                    </v-alert>

                    <!-- Default raw details if any -->
                    <div v-if="step.details.length">
                      <v-divider class="my-4" />
                      <div class="text-caption grey--text mb-2">
                        Métadonnées techniques
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-for="(detail, detailIndex) in step.details"
                          :key="detailIndex"
                        >
                          <v-expansion-panel-header>
                            Détails techniques
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre class="code-block">{{
                              prettyJson(detail)
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>

                  <!-- SPECIAL DISPLAY FOR TITRE STEP -->
                  <div v-else-if="step.name === 'generate_post_title'">
                    <div class="text-body-1 mb-2">
                      Titre généré :
                      <strong class="ml-1">{{ generatedTitle || "—" }}</strong>
                    </div>

                    <div v-if="llmConfigs.stage4_model" class="mt-4 mb-4">
                      <div class="text-caption grey--text mb-2">
                        Configuration LLM (Stage 4)
                      </div>
                      <div class="text-body-2 mb-2">
                        Modèle : <strong>{{ llmConfigs.stage4_model }}</strong>
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-if="llmConfigs.stage4_system_prompt"
                        >
                          <v-expansion-panel-header class="text-caption"
                            >System Prompt</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <pre class="code-block mt-2">{{
                              llmConfigs.stage4_system_prompt
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel v-if="llmConfigs.stage4_user_prompt">
                          <v-expansion-panel-header class="text-caption"
                            >User Prompt Template</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <pre class="code-block mt-2">{{
                              llmConfigs.stage4_user_prompt
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>

                    <!-- Default raw details if any -->
                    <div v-if="step.details.length">
                      <v-divider class="my-4" />
                      <div class="text-caption grey--text mb-2">
                        Métadonnées techniques
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-for="(detail, detailIndex) in step.details"
                          :key="detailIndex"
                        >
                          <v-expansion-panel-header>
                            Détails techniques
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre class="code-block">{{
                              prettyJson(detail)
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>

                  <!-- SPECIAL DISPLAY FOR CATEGORY/TAGS STEP -->
                  <div v-else-if="step.name === 'select_category_and_tags'">
                    <v-row dense>
                      <v-col cols="12" md="4">
                        <div class="text-caption text--secondary">
                          Catégorie trouvée
                        </div>
                        <div class="text-body-2 mb-3">
                          {{ selectedCategory || "—" }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <div class="text-caption text--secondary">
                          Tags trouvés
                        </div>
                        <div
                          v-if="selectedTags && selectedTags.length"
                          class="d-flex flex-wrap mb-3"
                        >
                          <v-chip
                            v-for="(tag, idx) in selectedTags"
                            :key="`tag-tab-${idx}`"
                            x-small
                            outlined
                            class="mr-1 mb-1"
                          >
                            {{ tag }}
                          </v-chip>
                        </div>
                        <div v-else class="grey--text text--darken-1 mb-3">
                          —
                        </div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <div class="text-caption text--secondary">
                          Post créé
                        </div>
                        <div class="text-body-2 mb-3">
                          <span v-if="uploadPostInfo && uploadPostInfo.post_id">
                            ID {{ uploadPostInfo.post_id }}
                            <span v-if="uploadPostInfo.post_url" class="ml-1">
                              (<a
                                :href="uploadPostInfo.post_url"
                                target="_blank"
                                rel="noopener noreferrer"
                                >ouvrir</a
                              >)
                            </span>
                          </span>
                          <span v-else>—</span>
                        </div>
                      </v-col>
                    </v-row>

                    <!-- Default raw details if any -->
                    <div v-if="step.details.length">
                      <v-divider class="my-4" />
                      <div class="text-caption grey--text mb-2">
                        Métadonnées techniques
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-for="(detail, detailIndex) in step.details"
                          :key="detailIndex"
                        >
                          <v-expansion-panel-header>
                            Détails techniques
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre class="code-block">{{
                              prettyJson(detail)
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>

                  <!-- SPECIAL DISPLAY FOR IMAGE STEP -->
                  <div v-else-if="step.name === 'get_image'">
                    <div class="mb-4">
                      <div class="text-subtitle-2 mb-2 d-flex align-center">
                        Image
                        <v-chip
                          x-small
                          label
                          dark
                          :color="imageMode.color"
                          :title="`Mode image : ${item.image_handler || 'generate'}`"
                          class="ml-2"
                        >
                          <v-icon x-small left>{{ imageMode.icon }}</v-icon>
                          {{ imageMode.label }}
                        </v-chip>
                      </div>
                      <div class="text-center border pa-2 rounded">
                        <v-img
                          v-if="imagePreviewUrl"
                          :src="imagePreviewUrl"
                          max-height="400"
                          contain
                          class="mb-2"
                        ></v-img>
                        <v-alert v-else dense outlined type="info" class="mb-2">
                          Aucune image.
                        </v-alert>
                        <!-- <div
                          v-if="imagePreviewUrl"
                          class="text-truncate grey--text text--darken-1 text-caption"
                        >
                          {{ imagePreviewUrl }}
                        </div>
                        <div
                          v-else-if="invalidImageValue"
                          class="grey--text text--darken-1 text-caption text-truncate"
                        >
                          Valeur ignorée (non-URL) : {{ invalidImageValue }}
                        </div> -->

                        <!-- URLs Display -->
                        <div class="mt-2 text-left">
                          <div v-if="cachedImageUrl" class="text-caption mb-1">
                            <!-- <span class="font-weight-bold">Cache URL:</span> -->
                            <a
                              :href="cachedImageUrl"
                              target="_blank"
                              class="text-decoration-none grey--text text--darken-2 ml-1 text-truncate d-inline-block"
                              style="max-width: 100%; vertical-align: bottom"
                            >
                              {{ cachedImageUrl }}
                            </a>
                          </div>
                          <div
                            v-if="payload.post_image_url"
                            class="text-caption"
                          >
                            <!-- <span class="font-weight-bold">Post URL:</span> -->
                            <a
                              :href="payload.post_image_url"
                              target="_blank"
                              class="text-decoration-none grey--text text--darken-2 ml-1 text-truncate d-inline-block"
                              style="max-width: 100%; vertical-align: bottom"
                            >
                              {{ payload.post_image_url }}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="text-caption text--secondary mb-1">
                      Image prompt
                    </div>
                    <div class="scroll-block code-block mb-3">
                      {{ imagePrompt || "—" }}
                    </div>

                    <div v-if="devMode">
                      <div class="text-caption text--secondary mb-1">
                        Query list
                      </div>
                      <div
                        v-if="queryList && queryList.length"
                        class="d-flex flex-wrap mb-3"
                      >
                        <v-chip
                          v-for="(query, idx) in queryList"
                          :key="`q-tab-${idx}`"
                          small
                          outlined
                          class="mr-2 mb-2"
                        >
                          {{ query }}
                        </v-chip>
                      </div>
                      <div v-else class="grey--text text--darken-1 mb-3">—</div>
                    </div>

                    <!-- Default raw details if any -->
                    <div v-if="step.details.length">
                      <v-divider class="my-4" />
                      <div class="text-caption grey--text mb-2">
                        Métadonnées techniques
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-for="(detail, detailIndex) in step.details"
                          :key="detailIndex"
                        >
                          <v-expansion-panel-header>
                            Détails techniques
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre class="code-block">{{
                              prettyJson(detail)
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>

                  <!-- SPECIAL DISPLAY FOR UPLOAD IMAGE STEP -->
                  <div v-else-if="step.name === 'upload_image'">
                    <div class="text-subtitle-2 mb-2">
                      Informations d'upload d'image
                    </div>
                    <div class="text-body-1 mb-4">
                      <span v-if="uploadImageInfo && uploadImageInfo.id">
                        Image ID : <strong>{{ uploadImageInfo.id }}</strong>
                      </span>
                      <span v-else class="grey--text"
                        >ID d'image non disponible.</span
                      >
                    </div>
                    <div class="text-body-1 mb-4">
                      <span v-if="uploadImageInfo && uploadImageInfo.url">
                        Image URL :
                        <a
                          :href="uploadImageInfo.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          >{{ uploadImageInfo.url }}</a
                        >
                      </span>
                      <span v-else class="grey--text"
                        >URL d'image non disponible.</span
                      >
                    </div>

                    <!-- Uploaded Image Preview -->
                    <div
                      v-if="uploadImageInfo && uploadImageInfo.url"
                      class="mt-4 mb-4"
                    >
                      <div class="text-subtitle-2 mb-2">Image hébergée</div>
                      <div class="text-center border pa-2 rounded">
                        <v-img
                          :src="uploadImageInfo.url"
                          max-height="400"
                          contain
                          class="mb-2"
                        ></v-img>
                        <div
                          class="text-truncate grey--text text--darken-1 text-caption"
                        >
                          {{ uploadImageInfo.url }}
                        </div>
                      </div>
                    </div>

                    <!-- Default raw details if any -->
                    <div v-if="step.details.length">
                      <v-divider class="my-4" />
                      <div class="text-caption grey--text mb-2">
                        Métadonnées techniques
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-for="(detail, detailIndex) in step.details"
                          :key="detailIndex"
                        >
                          <v-expansion-panel-header>
                            Détails techniques
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre class="code-block">{{
                              prettyJson(detail)
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>

                  <!-- SPECIAL DISPLAY FOR PUBLICATION STEP -->
                  <div v-else-if="step.name === 'upload_post'">
                    <!-- <div class="text-subtitle-2 mb-2">
                      Informations de publication
                    </div> -->
                    <div class="text-body-1 mb-4">
                      <span v-if="uploadPostInfo && uploadPostInfo.post_id">
                        Le post a bien été créé avec l'ID :
                        <strong>{{ uploadPostInfo.post_id }}</strong>
                        <v-btn
                          v-if="uploadPostAdminUrl"
                          text
                          small
                          color="primary"
                          class="ml-2"
                          :href="uploadPostAdminUrl"
                          target="_blank"
                        >
                          Ouvrir le post dans l'admin
                          <v-icon small class="ml-1">{{
                            icons.mdiOpenInNew
                          }}</v-icon>
                        </v-btn>
                        <v-btn
                          v-if="uploadPostInfo.post_url"
                          text
                          small
                          color="primary"
                          class="ml-2"
                          :href="uploadPostInfo.post_url"
                          target="_blank"
                        >
                          Ouvrir le post en front
                          <v-icon small class="ml-1">{{
                            icons.mdiOpenInNew
                          }}</v-icon>
                        </v-btn>
                      </span>
                      <span v-else class="grey--text"
                        >Post non encore créé ou ID manquant.</span
                      >
                    </div>

                    <!-- <v-divider class="mb-4" /> -->

                    <!-- Post Content & Extras Container -->
                    <div class="mb-4">
                      <!-- <div class="text-subtitle-2 mb-2">Aperçu du post</div> -->
                      <div class="html-preview scroll-block-large">
                        <!-- Chapô -->
                        <div
                          v-if="postExtras && postExtras.excerpt"
                          class="mb-6"
                        >
                          <h2 class="mb-1"># Chapô :</h2>
                          <div
                            class="text-subtitle-1 font-bold pl-3 border-left-primary"
                          >
                            <!-- font-italic -->
                            {{ postExtras.excerpt }}
                          </div>
                        </div>

                        <!-- Summary Block -->
                        <div
                          v-if="
                            postExtras &&
                            (postExtras.summary_bloc_bullet_point_1 ||
                              postExtras.summary_bloc_bullet_point_2 ||
                              postExtras.summary_bloc_bullet_point_3)
                          "
                        >
                          <h2># Bloc résumé :</h2>
                          <div class="mb-4 pa-4 grey lighten-4 rounded">
                            <ul class="pl-4 mb-0 text-body-2">
                              <li v-if="postExtras.summary_bloc_bullet_point_1">
                                {{ postExtras.summary_bloc_bullet_point_1 }}
                              </li>
                              <li v-if="postExtras.summary_bloc_bullet_point_2">
                                {{ postExtras.summary_bloc_bullet_point_2 }}
                              </li>
                              <li v-if="postExtras.summary_bloc_bullet_point_3">
                                {{ postExtras.summary_bloc_bullet_point_3 }}
                              </li>
                            </ul>
                          </div>
                        </div>

                        <!-- <v-divider
                          v-if="
                            (postExtras &&
                              (postExtras.excerpt ||
                                postExtras.summary_bloc_bullet_point_1)) &&
                            payload.post_content
                          "
                          class="mb-6"
                        ></v-divider> -->

                        <!-- Content Body -->
                        <div v-if="payload.post_content">
                          <h2 class="mb-2"># Corps du texte :</h2>
                          <div v-html="payload.post_content"></div>
                        </div>
                        <div
                          v-else-if="
                            !postExtras ||
                            (!postExtras.excerpt &&
                              !postExtras.summary_bloc_bullet_point_1)
                          "
                          class="text-center pa-4 grey--text"
                        >
                          Contenu non disponible.
                        </div>
                      </div>
                    </div>

                    <!-- Debug payload (Only in DEV MODE) -->
                    <div v-if="devMode && debugPostData" class="mb-4">
                      <div class="text-caption grey--text mb-1">
                        Debug payload (Dev Mode)
                      </div>
                      <pre class="code-block code-block--dark">{{
                        prettyJson(debugPostData)
                      }}</pre>
                    </div>

                    <!-- Default raw details if any -->
                    <div v-if="step.details.length">
                      <v-divider class="my-4" />
                      <div class="text-caption grey--text mb-2">
                        Métadonnées techniques
                      </div>
                      <v-expansion-panels multiple focusable>
                        <v-expansion-panel
                          v-for="(detail, detailIndex) in step.details"
                          :key="detailIndex"
                        >
                          <v-expansion-panel-header>
                            Détails techniques
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre class="code-block">{{
                              prettyJson(detail)
                            }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>

                  <!-- DEFAULT DISPLAY FOR OTHER STEPS -->
                  <div v-else>
                    <v-alert
                      v-if="!step.details.length"
                      dense
                      outlined
                      type="info"
                    >
                      Pas de métadonnées pour cette étape.
                    </v-alert>
                    <v-expansion-panels v-else multiple focusable>
                      <v-expansion-panel
                        v-for="(detail, detailIndex) in step.details"
                        :key="detailIndex"
                      >
                        <v-expansion-panel-header>
                          Détails techniques
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <pre class="code-block">{{ prettyJson(detail) }}</pre>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </div>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </div>

        <v-expansion-panels focusable class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-header>
              Payload d'entrée
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-alert
                v-if="!inputPayload"
                dense
                outlined
                type="info"
                class="mb-0"
              >
                Aucune donnée de payload d'entrée disponible.
              </v-alert>
              <v-simple-table v-else dense class="input-payload-table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Champ technique</th>
                    <th>Valeur</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="font-weight-medium" style="width: 200px">Email utilisateur</td>
                    <td class="text-caption" style="font-family: monospace; width: 260px">user_email</td>
                    <td>{{ inputPayload.user_email || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Site ID</td>
                    <td class="text-caption" style="font-family: monospace">site_id</td>
                    <td>{{ inputPayload.site_id != null ? inputPayload.site_id : '—' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Topic</td>
                    <td class="text-caption" style="font-family: monospace">topic</td>
                    <td>{{ inputPayload.topic || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Topic = Titre</td>
                    <td class="text-caption" style="font-family: monospace">topic_is_title</td>
                    <td>
                      <template v-if="inputPayload.topic_is_title != null">
                        {{ inputPayload.topic_is_title ? '✓ Oui' : '✗ Non' }}
                      </template>
                      <span v-else>—</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Template custom ID</td>
                    <td class="text-caption" style="font-family: monospace">aif_custom_prompt_template_id</td>
                    <td>{{ inputPayload.aif_custom_prompt_template_id != null ? inputPayload.aif_custom_prompt_template_id : '—' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Statut demandé</td>
                    <td class="text-caption" style="font-family: monospace">status</td>
                    <td>
                      <v-chip v-if="inputPayload.status" x-small outlined>
                        {{ inputPayload.status }}
                      </v-chip>
                      <span v-else>—</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Image handler</td>
                    <td class="text-caption" style="font-family: monospace">image_handler</td>
                    <td>{{ inputPayload.image_handler || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Image ID</td>
                    <td class="text-caption" style="font-family: monospace">image_id</td>
                    <td>{{ inputPayload.image_id != null ? inputPayload.image_id : '—' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Image URL</td>
                    <td class="text-caption" style="font-family: monospace">image_url</td>
                    <td>
                      <a
                        v-if="inputPayload.image_url && isLikelyUrl(inputPayload.image_url)"
                        :href="inputPayload.image_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-truncate d-inline-block"
                        style="max-width: 400px; vertical-align: bottom"
                      >
                        {{ inputPayload.image_url }}
                      </a>
                      <span
                        v-else-if="inputPayload.image_url"
                        class="text-truncate d-inline-block grey--text text--darken-2"
                        style="max-width: 400px; vertical-align: bottom"
                      >
                        {{ inputPayload.image_url }}
                      </span>
                      <span v-else>—</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Catégorie forcée (ID)</td>
                    <td class="text-caption" style="font-family: monospace">forced_category_id</td>
                    <td>{{ inputPayload.forced_category_id != null ? inputPayload.forced_category_id : '—' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Auteur (ID)</td>
                    <td class="text-caption" style="font-family: monospace">author_id</td>
                    <td>{{ inputPayload.author_id != null ? inputPayload.author_id : '—' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Programmé</td>
                    <td class="text-caption" style="font-family: monospace">scheduled</td>
                    <td>{{ inputPayload.scheduled ? formatDateHumanReadable(inputPayload.scheduled) : '—' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Origine RMR</td>
                    <td class="text-caption" style="font-family: monospace">rmr_origin_page_id</td>
                    <td>
                      {{ inputPayload.rmr_origin_page_name || '—' }}
                      <span v-if="inputPayload.rmr_origin_page_id" class="grey--text text--darken-1">
                        (ID: {{ inputPayload.rmr_origin_page_id }})
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Agrégé</td>
                    <td class="text-caption" style="font-family: monospace">is_aggregated</td>
                    <td>
                      <template v-if="inputPayload.is_aggregated != null">
                        {{ inputPayload.is_aggregated ? '✓ Oui' : '✗ Non' }}
                      </template>
                      <span v-else>—</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <!-- Sources list -->
              <div
                v-if="inputPayload && inputPayload.sources && inputPayload.sources.length"
                class="mt-4"
              >
                <div class="text-subtitle-2 mb-2 d-flex align-center">
                  Sources d'entrée
                  <v-chip small class="ml-2" outlined>
                    {{ inputPayload.sources.length }} source{{
                      inputPayload.sources.length > 1 ? 's' : ''
                    }}
                  </v-chip>
                </div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Type</th>
                      <th>Plage</th>
                      <th>URL</th>
                      <th>Contenu</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(src, idx) in inputPayload.sources"
                      :key="`ip-src-${idx}`"
                    >
                      <td>{{ idx + 1 }}</td>
                      <td>
                        <v-chip x-small outlined>
                          {{ src.source_type || '—' }}
                        </v-chip>
                      </td>
                      <td>
                        <v-chip
                          v-if="sourceTimecode(src)"
                          x-small
                          outlined
                        >
                          {{ sourceTimecode(src) }}
                        </v-chip>
                        <span v-else class="grey--text">—</span>
                      </td>
                      <td>
                        <a
                          v-if="src.url && isLikelyUrl(src.url)"
                          :href="src.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-truncate d-inline-block"
                          style="max-width: 400px; vertical-align: bottom"
                        >
                          {{ src.url }}
                        </a>
                        <span
                          v-else-if="src.url"
                          class="text-truncate d-inline-block grey--text text--darken-2"
                          style="max-width: 400px; vertical-align: bottom"
                        >
                          {{ src.url }}
                        </span>
                        <span v-else class="grey--text">—</span>
                      </td>
                      <td>
                        <span v-if="src.content" class="text-truncate d-inline-block" style="max-width: 200px">
                          {{ src.content.substring(0, 100) }}{{ src.content.length > 100 ? '…' : '' }}
                        </span>
                        <span v-else class="grey--text">—</span>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-expansion-panels focusable>
          <v-expansion-panel>
            <v-expansion-panel-header>Logs</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="stepsWithLogs.length">
                <div class="text-subtitle-1 mb-2">Log par étape</div>
                <v-expansion-panels accordion multiple>
                  <v-expansion-panel
                    v-for="step in stepsWithLogs"
                    :key="`log-${step.name}`"
                  >
                    <v-expansion-panel-header>
                      {{ step.label }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <pre class="code-block code-block--dark">{{
                        step.logs
                      }}</pre>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
                <v-divider class="my-4"></v-divider>
              </div>

              <v-expansion-panels accordion>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    Log complet
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <pre class="code-block code-block--dark">{{
                      (payload && payload.logs) || (payload && payload.error ? ('❌ Erreur : ' + payload.error) : 'Aucun log disponible.')
                    }}</pre>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <div
                v-if="extraSteps && extraSteps.length"
                class="mt-4"
              >
                <v-divider class="mb-4"></v-divider>
                <div class="text-subtitle-1 mb-2">Autres étapes</div>
                <v-expansion-panels accordion multiple>
                  <v-expansion-panel
                    v-for="step in extraSteps"
                    :key="step.name"
                  >
                    <v-expansion-panel-header>
                      {{ step.label }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div
                        v-if="!step.details.length"
                        class="text-caption grey--text"
                      >
                        Aucun détail disponible.
                      </div>
                      <div v-else>
                        <div
                          v-for="(detail, i) in step.details"
                          :key="i"
                          class="mb-2"
                        >
                          <pre class="code-block">{{ prettyJson(detail) }}</pre>
                        </div>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import {
  computed,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
} from "@vue/composition-api";
import { Konami, KonamiManager } from "@/utils/konami";
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiCheckCircle,
  mdiAutorenew,
  mdiClockOutline,
  mdiOpenInNew,
  mdiCog,
  mdiAlertCircleOutline,
  mdiFolderOutline,
  mdiRestart,
} from "@mdi/js";
import { aifImageModeFor } from "@/utils/aifImageModes";
import { buildPostAdminUrl } from "@/utils/siteConnectorAdminUrl";

const orderedSteps = [
  "init",
  "get_sources_text",
  "analyse_sources",
  "generate_post_content",
  "generate_post_extra",
  "generate_post_internal_links",
  "insert_video_embed",
  "generate_post_title",
  "select_category_and_tags",
  "get_image",
  "upload_image",
  "upload_post",
];

const stepNameMappings = {
  init: "Setup",
  get_sources_text: "Sources",
  analyse_sources: "Analyse",
  generate_post_content: "Contenu",
  generate_post_internal_links: "Maillage interne",
  insert_video_embed: "Embed vidéo",
  generate_post_extra: "Extras",
  generate_post_title: "Titre",
  select_category_and_tags: "Catégorie & tags",
  get_image: "Image",
  upload_image: "Upload image",
  upload_post: "Publication",
};

export default {
  props: {
    item: {
      type: Object,
      required: true,
    },
    payload: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const activeStep = ref(0);
    const devMode = ref(false);
    let konamiManager = null;
    const instance = getCurrentInstance();

    const parseDateAsUtc = (dateVal) => {
      if (!dateVal || typeof dateVal !== "string") return new Date(NaN);
      let dateString = dateVal.replace(" ", "T");
      if (!dateString.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(dateString)) {
        dateString += "Z";
      }
      return new Date(dateString);
    };

    const formatDateHumanReadable = (val) => {
      if (!val) return "—";
      const d = parseDateAsUtc(val);
      if (isNaN(d.getTime())) return "—";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}h${minutes}`;
    };

    const getDuration = (start, end) => {
      if (!start || !end) return null;
      const s = parseDateAsUtc(start).getTime();
      const e = parseDateAsUtc(end).getTime();
      if (isNaN(s) || isNaN(e)) return null;
      const diff = Math.floor((e - s) / 1000);
      const minutes = Math.floor(diff / 60);
      const seconds = diff % 60;
      if (minutes > 0) {
        return `${minutes} min${seconds ? ` ${seconds}s` : ""}`;
      }
      return `${seconds}s`;
    };

    const transformUrl = (url, postId) => {
      const parsed = new URL(url);
      // Preserve subpath (e.g. /us) from site_url — parsed.origin alone would strip it
      const base = `${parsed.origin}${parsed.pathname.replace(/\/$/, "")}`;
      if (parsed.origin.includes("marmiton")) {
        return `${parsed.origin}/reloaded/manage/story/list#/13/list/edit/${postId}`;
      } else if (parsed.origin.includes("lesnum")) {
        return `https://redaction2.lesnums.com/publication/news/${postId}/edit`;
      }
      return `${base}/wp-admin/post.php?post=${postId}&action=edit`;
    };

    const prettyJson = (obj) => {
      try {
        return JSON.stringify(obj, null, 2);
      } catch (error) {
        return String(obj);
      }
    };

    const parseSteps = (stepsJson) => {
      if (!stepsJson) return [];
      let parsed = stepsJson;
      if (typeof stepsJson === "string") {
        try {
          parsed = JSON.parse(stepsJson);
        } catch (error) {
          return [];
        }
      }
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(Boolean);
    };

    const buildStepMap = (stepsJson) => {
      const parsedSteps = parseSteps(stepsJson);
      const map = new Map();
      parsedSteps.forEach((entry) => {
        if (!entry) return;
        const stepKey = entry.step || "Autre";
        if (!map.has(stepKey)) {
          map.set(stepKey, {
            name: stepKey,
            start: null,
            end: null,
            logs: "",
            details: [],
          });
        }
        const current = map.get(stepKey);
        if (entry.start) current.start = entry.start;
        if (entry.end) current.end = entry.end;
        const rest = { ...entry };
        delete rest.step;
        delete rest.start;
        delete rest.end;
        if (rest.logs) {
          current.logs += rest.logs;
          delete rest.logs;
        }
        if (Object.keys(rest).length) {
          current.details.push(rest);
        }
      });

      return map;
    };

    const normalizeSteps = (stepsJson) => {
      const map = buildStepMap(stepsJson);

      // Final list: ONLY ordered steps

      // Extra keys are now handled by extraSteps computed

      return orderedSteps.map((key) => {
        // Get existing data or create default
        const base = map.get(key) || {
          name: key,
          start: null,
          end: null,
          logs: "",
          details: [],
        };
        // Status logic
        let status = "pending";
        if (base.start) {
          status = base.end ? "done" : "running";
        }
        return {
          ...base,
          label: stepNameMappings[key] || formatStepName(key),
          status,
          duration: getDuration(base.start, base.end),
        };
      });
    };
    const extraSteps = computed(() => {
      const map = buildStepMap(props.payload.steps_json);
      const dataKeys = Array.from(map.keys());
      const extraKeys = dataKeys.filter((k) => !orderedSteps.includes(k));
      return extraKeys.map((key) => {
        const base = map.get(key);
        return {
          ...base,
          label: stepNameMappings[key] || formatStepName(key),
        };
      });
    });
    const normalizedSteps = computed(() =>
      normalizeSteps(props.payload.steps_json)
    );

    const visibleSteps = computed(() => {
      if (devMode.value) return normalizedSteps.value;
      return normalizedSteps.value.filter((s) => s.name !== "init");
    });

    const stepsWithLogs = computed(() => [
      ...normalizedSteps.value.filter((s) => s.logs),
      ...extraSteps.value.filter((s) => s.logs),
    ]);

    const parsedSteps = computed(() => parseSteps(props.payload.steps_json));

    const stepsByName = computed(() => {
      const map = {};
      parsedSteps.value.forEach((entry) => {
        const key = entry.step || "Autre";
        if (!map[key]) map[key] = [];
        map[key].push(entry);
      });
      return map;
    });

    const lastEntryFor = (keys) => {
      for (const key of keys) {
        const list = stepsByName.value[key];
        if (list && list.length) return list[list.length - 1];
      }
      return null;
    };

    watch(
      visibleSteps,
      (steps) => {
        if (!steps || !steps.length) {
          activeStep.value = 0;
          return;
        }
        const currentIndex = steps.findIndex(
          (s) => s.name === props.item.current_step
        );
        if (currentIndex !== -1) {
          activeStep.value = currentIndex;
        } else if (activeStep.value >= steps.length) {
          activeStep.value = 0;
        }
      },
      { immediate: true }
    );

    const nextStep = () => {
      if (activeStep.value + 1 < visibleSteps.value.length) {
        activeStep.value += 1;
      }
    };

    const prevStep = () => {
      if (activeStep.value > 0) {
        activeStep.value -= 1;
      }
    };

    const stepStatusIcon = (status) => {
      if (status === "done") return mdiCheckCircle;
      if (status === "running") return mdiAutorenew;
      return mdiClockOutline;
    };

    const stepStatusColor = (status) => {
      if (status === "done") return "success";
      if (status === "running") return "info";
      return "grey";
    };

    const isStepDisabled = (step) => {
      const currentIndex = visibleSteps.value.findIndex(
        (s) => s.name === props.item.current_step
      );
      if (currentIndex === -1) return false; // If current step not found, enable all
      const stepIndex = visibleSteps.value.findIndex(
        (s) => s.name === step.name
      );
      return stepIndex > currentIndex;
    };

    const getStepTopBorderColor = (step) => {
      const currentIndex = visibleSteps.value.findIndex(
        (s) => s.name === props.item.current_step
      );
      const stepIndex = visibleSteps.value.findIndex(
        (s) => s.name === step.name
      );

      if (stepIndex < currentIndex) {
        return "var(--v-success-base)";
      }
      if (stepIndex === currentIndex) {
        return statusColor(props.item.status);
      }
      return null;
    };

    const statusColor = (status) => {
      switch (status) {
        case "processing":
          return "var(--v-info-base)";
        case "published":
        case "success":
          return "var(--v-success-base)";
        case "error":
          return "var(--v-error-base)";
        default:
          return "grey";
      }
    };

    const isLikelyUrl = (val) => {
      if (!val || typeof val !== "string") return false;
      return /^(https?:)?\/\//i.test(val) || val.startsWith("data:image");
    };

    const sourcesCount = computed(() =>
      Array.isArray(props.payload.sources) ? props.payload.sources.length : 0
    );

    const templateId = computed(
      () =>
        Number(
          props.payload?.aif_custom_prompt_template_id ||
            props.item?.aif_custom_prompt_template_id ||
            0
        ) || null
    );
    const templateName = computed(
      () =>
        props.payload?.aif_custom_prompt_template_name ||
        `#${templateId.value}`
    );
    const templateRoute = computed(() => ({
      name: "admin-prompt-config",
      query: {
        site_id: String(props.item.site_id),
        template_id: String(templateId.value),
      },
    }));
    const canOpenTemplateConfig = computed(
      () =>
        instance.proxy.$can("manage", "admin") ||
        instance.proxy.$can("manage", "ai_factory")
    );

    const imagePreviewUrl = computed(() => {
      // Check if created today
      let isCreatedToday = false;
      if (props.item.created) {
        const createdDate = parseDateAsUtc(props.item.created);
        const now = new Date();
        isCreatedToday =
          createdDate.getDate() === now.getDate() &&
          createdDate.getMonth() === now.getMonth() &&
          createdDate.getFullYear() === now.getFullYear();
      }

      if (isCreatedToday && cachedImageUrl.value) {
        return cachedImageUrl.value;
      }

      const candidates = [
        props.payload.image_url,
        props.payload.post_image_url,
        props.item?.image_url,
        cachedImageUrl.value,
      ];
      const found = candidates.find((c) => isLikelyUrl(c));
      return found || null;
    });

    const cachedImageUrl = computed(() => {
      const imageEntry = lastEntryFor(["get_image"]);
      const cachedImagePath = imageEntry?.path_to_cached_image_file;
      if (cachedImagePath) {
        const filename = cachedImagePath.split("/").pop();
        if (filename) {
          return `${process.env.VUE_APP_API_URL.replace(
            /[/]+$/,
            ""
          )}/cache-images/${filename}`;
        }
      }
      return null;
    });
    const invalidImageValue = computed(() => {
      const candidates = [
        props.payload.post_image_url,
        props.payload.image_url,
        props.item?.image_url,
      ];
      const found = candidates.find((c) => c && !isLikelyUrl(c));
      return found || null;
    });

    const postExtrasEntry = computed(() =>
      lastEntryFor(["generate_post_extra"])
    );
    const postExtras = computed(
      () => postExtrasEntry.value?.post_extra_dict || null
    );

    const postTitleEntry = computed(() =>
      lastEntryFor(["generate_post_title"])
    );
    const generatedTitle = computed(
      () =>
        postTitleEntry.value?.post_title ||
        postExtras.value?.title ||
        props.payload?.post_title ||
        ""
    );

    // Uniquement le prompt REELLEMENT envoye au modele, enregistre par l'etape
    // get_image. Pas de repli sur postExtras : depuis que l'image est generee
    // par le step AIF_IMAGE, le image_prompt du stage3 est mort, et l'afficher
    // ferait croire qu'il a servi.
    const imagePromptEntry = computed(() => lastEntryFor(["get_image"]));
    const imagePrompt = computed(
      () => imagePromptEntry.value?.image_prompt || null
    );
    const imageMode = computed(() =>
      aifImageModeFor(props.item?.image_handler)
    );

    const queryList = computed(() => postExtras.value?.query_list || []);

    const selectCatEntry = computed(() =>
      lastEntryFor(["select_category_and_tags"])
    );
    const selectedCategory = computed(() => {
      const entry = selectCatEntry.value;
      if (!entry) return null;
      const key = Object.keys(entry).find((k) => k.includes("category_found"));
      return key ? entry[key] : null;
    });
    const selectedTags = computed(() => {
      const entry = selectCatEntry.value;
      if (!entry) return null;
      const key = Object.keys(entry).find((k) => k.includes("tags_found"));
      const tags = key ? entry[key] : null;
      return Array.isArray(tags) ? tags : null;
    });

    const initEntry = computed(() => lastEntryFor(["init"]));
    const siteConnector = computed(
      () => initEntry.value?.site_connector || null
    );

    const uploadPostEntry = computed(() => lastEntryFor(["upload_post"]));
    const uploadPostInfo = computed(() => {
      if (!uploadPostEntry.value) return null;
      const { post_id, post_url } = uploadPostEntry.value;
      return post_id || post_url ? { post_id, post_url } : null;
    });
    const uploadPostAdminUrl = computed(() => {
      if (!uploadPostInfo.value?.post_id || !uploadPostInfo.value?.post_url) {
        return null;
      }

      return buildPostAdminUrl({
        postUrl: uploadPostInfo.value.post_url,
        postId: uploadPostInfo.value.post_id,
        siteConnector: siteConnector.value,
      });
    });

    const uploadImageEntry = computed(() => lastEntryFor(["upload_image"]));
    const uploadImageInfo = computed(() => {
      if (!uploadImageEntry.value) return null;
      const { post_image_id, post_image_url } = uploadImageEntry.value;
      return post_image_id || post_image_url
        ? { id: post_image_id, url: post_image_url }
        : null;
    });

    const internalLinksEntry = computed(() =>
      lastEntryFor(["generate_post_internal_links"])
    );
    const internalLinksInfo = computed(() => {
      const entry = internalLinksEntry.value;
      if (!entry) {
        return {
          applied: 0,
          candidates_count: 0,
          integratedArticles: [],
          topics: [],
        };
      }
      const integratedArticles = Array.isArray(
        entry.internal_links_integrated_articles
      )
        ? entry.internal_links_integrated_articles
        : [];

      return {
        applied: entry.internal_links_applied || 0,
        candidates_count: entry.internal_links_candidates_count || 0,
        integratedArticles,
        topics: entry.related_topics || [],
      };
    });

    const debugPostData = computed(
      () => uploadPostEntry.value?.debug_post_data || null
    );
    const llmConfigs = computed(() => initEntry.value?.site_llm_configs || {});

    const inputPayload = computed(() => props.payload?.input_payload || null);

    onMounted(() => {
      konamiManager = new KonamiManager(instance.proxy, [
        new Konami([68, 69, 86], () => {
          devMode.value = !devMode.value; // Toggle
        }),
      ]);
    });

    onBeforeUnmount(() => {
      if (konamiManager && typeof konamiManager.destroy === "function") {
        konamiManager.destroy();
      }
    });

    const getStepStage = (stepName) => {
      const mapping = {
        analyse_sources: 1,
        generate_post_content: 2,
        generate_post_extra: 3,
        generate_post_title: 4,
      };
      return mapping[stepName] || null;
    };

    /**
     * Le time code d'une source video, quand la generation a porte sur un
     * passage borne (T4). Absent sur une source ecrite et sur une video prise
     * en entier — dans les deux cas il n'y a rien a afficher.
     */
    const sourceTimecode = (source) => {
      const start = source?.start_s;
      const end = source?.end_s;
      if (start === null || start === undefined) return "";
      if (end === null || end === undefined) return "";
      const mmss = (seconds) => {
        const total = Math.max(0, Math.round(Number(seconds) || 0));
        return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(
          total % 60
        ).padStart(2, "0")}`;
      };
      return `${mmss(start)} – ${mmss(end)}`;
    };

    /**
     * Le texte que le LLM a REELLEMENT recu pour cette source, retrouve dans
     * `payload.sources_text`.
     *
     * `get_sources_text` numerote ses blocs `#i` sur le rang de la source, et
     * les enrobe differemment selon qu'il s'agit d'une transcription video ou
     * d'un contenu ecrit. On rejoue cette numerotation plutot que de stocker
     * une seconde copie du texte dans `sources_json` : deux copies finiraient
     * par diverger, et le transcript n'a aucune raison de transiter par le
     * navigateur.
     */
    const sourceUsedText = (index) => {
      const consolidated = props.payload?.sources_text;
      if (!consolidated) return "";
      const number = index + 1;
      const patterns = [
        `transcription video #${number}`,
        `contenu source #${number}`,
      ];
      for (const label of patterns) {
        // L'espace final est significatif : sans lui, la recherche de `#1`
        // accrocherait le bloc `#10` quand le premier a ete saute.
        const start = consolidated.indexOf(`<debut de la ${label} `);
        const startWritten = consolidated.indexOf(`<debut du ${label} `);
        const from = start !== -1 ? start : startWritten;
        if (from === -1) continue;
        const bodyFrom = consolidated.indexOf("\n", from);
        const to = consolidated.indexOf(`<fin de la ${label}>`, from);
        const toWritten = consolidated.indexOf(`<fin du ${label}>`, from);
        const until = to !== -1 ? to : toWritten;
        if (bodyFrom === -1 || until === -1) continue;
        return consolidated.slice(bodyFrom + 1, until).trim();
      }
      return "";
    };

    const truncateText = (text, length = 200) => {
      if (!text) return "";
      const cleanText = text.replace(/\s+/g, " ").trim();
      if (cleanText.length <= length) return cleanText;
      return `${cleanText.substring(0, length)}...`;
    };

    return {
      activeStep,
      normalizedSteps,
      extraSteps,
      nextStep,
      prevStep,
      prettyJson,
      stepStatusIcon,
      stepStatusColor,
      formatDateHumanReadable,
      statusColor,
      sourcesCount,
      templateId,
      templateName,
      templateRoute,
      canOpenTemplateConfig,
      imagePreviewUrl,
      cachedImageUrl,
      invalidImageValue,
      isLikelyUrl,
      formatStepName,
      postExtras,
      generatedTitle,
      imagePrompt,
      imageMode,
      queryList,
      selectedCategory,
      selectedTags,
      uploadPostInfo,
      uploadPostAdminUrl,
      uploadImageInfo,
      internalLinksInfo,
      debugPostData,
      devMode,
      llmConfigs,
      inputPayload,
      visibleSteps,
      stepsWithLogs,
      getStepStage,
      isStepDisabled,
      getStepTopBorderColor,
      truncateText,
      sourceTimecode,
      sourceUsedText,
      icons: {
        mdiChevronLeft,
        mdiChevronRight,
        mdiCheckCircle,
        mdiAutorenew,
        mdiClockOutline,
        mdiOpenInNew,
        mdiCog,
        mdiAlertCircleOutline,
        mdiFolderOutline,
        mdiRestart,
      },
    };
  },
};

function formatStepName(step) {
  if (!step) return "—";
  return stepNameMappings[step] || step.replace(/_/g, " ");
}
</script>
<style scoped>
.dialog-details .chips-row .v-chip {
  height: 26px;
}

.dialog-details .header-image {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  overflow: hidden;
}

.dialog-details .header-image.placeholder {
  height: 140px;
}

.header-text-wrap {
  word-break: normal;
  overflow-wrap: break-word;
  word-wrap: break-word;
}

.step-wrapper {
  background: #f7f7f9;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.step-tabs {
  min-height: 48px;
}

/* Custom styling for tabs */
.disabled-step {
  opacity: 0.5;
  pointer-events: none;
}

.custom-tabs ::v-deep .v-tab {
  min-width: 80px; /* Increased from 60px */
  padding: 0 8px;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
}

.custom-tabs ::v-deep .v-tab--active {
  border-bottom: 3px solid var(--v-primary-base) !important;
}

.custom-tabs ::v-deep .v-tab:not(.v-tab--active) .tab-content {
  opacity: 0.7;
}

.custom-tabs ::v-deep .v-tab:not(.v-tab--active) .tab-label {
  font-size: 0.8rem; /* Increased from 0.7rem */
  max-width: 150px; /* Increased from 60px */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.custom-tabs ::v-deep .v-tab:not(.v-tab--active) .tab-icon {
  font-size: 14px; /* Smaller icon */
}

.custom-tabs ::v-deep .v-tab--active .tab-label {
  font-size: 0.95rem; /* Normal size for active */
  font-weight: 600;
}

.code-block {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: monospace;
  font-size: 0.85rem;
}

.code-block--dark {
  background: #212121;
  color: #f5f5f5;
  border-color: #424242;
}

.scroll-block {
  max-height: 240px;
  overflow-y: auto;
}

.scroll-block-large {
  max-height: 300px;
  overflow-y: auto;
}

.border-left-primary {
  border-left: 4px solid var(--v-primary-base);
}

.html-preview {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
}

.text-decoration-none {
  text-decoration: none;
}
</style>
